import {
  ButtonBase,
  Popover,
  TextField,
  TextFieldProps,
  Tooltip
} from '@material-ui/core';
import { groupBy } from 'lodash';
import { Braces, PlusSquare } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useUnsafeTranslation } from '../../i18next';
import { FlexContainer } from '../../layout/Flex';
import { toSearchTermRegexp } from '../../services/user';
import { useTheme } from '../../themes';
import * as UX from '../../features/Experiences/components/Builders/newux/kit';
import { TokenDefinition, TokenSelectorDialog } from '../MarkdownText';

/**
 * Compact, grouped variable picker in a popover. Reused by the markdown editor's
 * "Text Variable" button. `onSelect` receives the token; the caller inserts it.
 */
export const TokenPickerPopover = ({
  anchorEl,
  tokens,
  onClose,
  onSelect
}: {
  anchorEl: HTMLElement | null;
  tokens: TokenDefinition[];
  onClose: () => void;
  onSelect: (def: TokenDefinition) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const [q, setQ] = useState('');
  useEffect(() => {
    if (anchorEl) setQ('');
  }, [anchorEl]);
  const groupEntries = useMemo(() => {
    const re = toSearchTermRegexp(q);
    const filtered = tokens.filter(
      (tk) => tk.token.match(re) || tk.label.match(re)
    );
    const grouped = Object.entries(groupBy(filtered, (tk) => tk.group));
    const participantLabel = t('Participant');
    const leadOrder = [participantLabel, 'Experience', 'Org'];
    return [
      ...leadOrder
        .map((label) => grouped.find(([group]) => group === label))
        .filter((entry): entry is [string, TokenDefinition[]] => !!entry),
      ...grouped.filter(([group]) => !leadOrder.includes(group))
    ];
  }, [q, tokens, t]);
  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <div
        style={{
          maxHeight: 520,
          overflowY: 'auto',
          padding: '8px 6px',
          minWidth: 240
        }}
      >
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            background: '#fff',
            padding: '4px 4px 8px'
          }}
        >
          <TextField
            type="search"
            fullWidth
            autoFocus
            value={q}
            onChange={(ev) => setQ(ev.target.value)}
            placeholder={t('Search variables')}
          />
        </div>
        {!groupEntries.length && (
          <div
            style={{
              padding: '6px 10px',
              fontSize: 13,
              color: '#8C9BAF'
            }}
          >
            {t('No variables match your search.')}
          </div>
        )}
        {groupEntries.map(([group, groupTokens]) => (
          <div key={group} style={{ marginBottom: 6 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '.04em',
                color: UX.tokens.color.grey500,
                padding: '6px 10px 4px'
              }}
            >
              {group === 'Org' ? t('Organization') : group}
            </div>
            {groupTokens.map((tk) => {
              // The default blue is reserved for Participant/Org/Experience —
              // every other actor's variables use that actor's own color.
              const isDefaultBlueGroup =
                tk.group === t('Participant') ||
                tk.group === 'Org' ||
                tk.group === 'Experience';
              return (
                <ButtonBase
                  key={`${tk.group}:${tk.token}`}
                  onClick={() => onSelect(tk)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    width: '100%',
                    height: 32,
                    padding: '0 10px',
                    borderRadius: 4,
                    justifyContent: 'flex-start',
                    fontSize: 14,
                    color: '#1A2538'
                  }}
                >
                  <Braces
                    size={14}
                    color={isDefaultBlueGroup ? UX.tokens.color.blue500 : tk.color}
                  />
                  {tk.label}
                </ButtonBase>
              );
            })}
          </div>
        ))}
      </div>
    </Popover>
  );
};

export const TextFieldWithReplacementTokens = ({
  value,
  onChange,
  tokens,
  variant = 'outlined',
  compact = false,
  ...props
}: Omit<TextFieldProps, 'variant' | 'onChange'> & {
  value: string;
  onChange: (nextValue: string) => void;
  tokens: TokenDefinition[];
  // Hardcoded - this is just here so that it's easier
  // to replace a normal TextField, where we might have
  // defined this. Pure convenience.
  variant?: 'outlined';
  // NEW_UX: render a compact inline "+" that opens an anchored dropdown of variables
  // instead of the full-screen TokenSelectorDialog. Same insert behavior.
  compact?: boolean;
}) => {
  const ref = useRef<HTMLInputElement>(null);
  const [dialogState, setDialogState] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const theme = useTheme();
  const { t } = useUnsafeTranslation();

  const [lastSelection, setLastSelection] = useState<[number, number] | null>(
    null
  );

  const captureSelection = () => {
    const input = ref.current;
    if (input) {
      setLastSelection([
        input.selectionStart || 0,
        input.selectionEnd === null
          ? input.selectionStart || 0
          : input.selectionEnd
      ]);
    }
  };

  // Shared insertion: place `{{ token }}` at the last cursor selection, or append.
  const insertToken = (def: TokenDefinition, opts?: string) => {
    const token = `{{ ${[def.token, opts].filter((x) => x).join(' | ')} }}`;
    const input = ref.current;
    if (lastSelection) {
      const characters = value.split('');
      const [start, end] = lastSelection;
      characters.splice(start, end - start, token);
      const nextValue = characters.join('');
      onChange(nextValue);
      setTimeout(() => {
        if (input) {
          input.focus();
          const nextCursor = start + token.length;
          input.setSelectionRange(nextCursor, nextCursor);
        }
      });
    } else {
      const nextValue = value + token;
      onChange(nextValue);
      setTimeout(() => {
        if (input) {
          input.focus();
          input.setSelectionRange(nextValue.length, nextValue.length);
        }
      });
    }
  };

  const endAdornment = compact ? (
    <ButtonBase
      style={{ alignSelf: props.multiline ? 'start' : 'auto' }}
      onClick={(e) => {
        captureSelection();
        setAnchorEl(e.currentTarget);
      }}
    >
      <Tooltip title={`${t('Insert text variable')}`}>
        <PlusSquare size={18} strokeWidth={2} color={'#0061C4'} />
      </Tooltip>
    </ButtonBase>
  ) : (
    <ButtonBase
      style={{ alignSelf: props.multiline ? 'start' : 'auto' }}
      onClick={() => {
        captureSelection();
        return setDialogState(true);
      }}
    >
      <Tooltip title={`${t('Insert text variable')}`}>
        <PlusSquare
          size={16}
          strokeWidth={1.8}
          color={theme.custom.colors.gray.w500}
        />
      </Tooltip>
    </ButtonBase>
  );
  return (
    <>
      <TextField
        {...props}
        variant={variant}
        value={value}
        onChange={(ev) => onChange(ev.target.value)}
        inputProps={{ ref }}
        InputProps={{
          ...props.InputProps,
          endAdornment: props.InputProps?.endAdornment ? (
            <FlexContainer spacing={0.5} alignItems="flex-start">
              {props.InputProps.endAdornment}
              {endAdornment}
            </FlexContainer>
          ) : (
            endAdornment
          )
        }}
      />
      {compact ? (
        <TokenPickerPopover
          anchorEl={anchorEl}
          tokens={tokens}
          onClose={() => setAnchorEl(null)}
          onSelect={(tk) => {
            insertToken(tk);
            setAnchorEl(null);
          }}
        />
      ) : (
        <TokenSelectorDialog
          open={!!dialogState}
          onClose={() => {
            setDialogState(false);
            setTimeout(() => {
              if (lastSelection && ref.current) {
                ref.current.focus();
                ref.current.setSelectionRange(...lastSelection);
              }
            });
          }}
          tokens={tokens}
          onSelect={(tk, opts) => {
            if (!dialogState) {
              return;
            }
            setTimeout(() => {
              setDialogState(false);
              insertToken(tk, opts);
            });
          }}
        />
      )}
    </>
  );
};
